import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { CheckCircle, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const TIERS = [
  {
    name: "Lite",
    tier: "lite" as const,
    price: "Free + 3%",
    aiCredit: "$1 of AI assist / mo",
    bullets: [
      "Up to 50 leads / 25 active clients",
      "1 user",
      "Stripe Connect with 3% per payment",
      "AI assist: $1 monthly gifted credit",
    ],
  },
  {
    name: "Pro",
    tier: "pro" as const,
    price: "$49 / mo",
    aiCredit: "$5 of AI assist / mo",
    bullets: [
      "Unlimited leads + clients",
      "5 users / 10 talent",
      "Drip campaigns + SMS",
      "0% platform fee",
      "AI assist: $5 monthly gifted credit",
    ],
  },
  {
    name: "Agency",
    tier: "agency" as const,
    price: "$199 / mo",
    aiCredit: "$20 of AI assist / mo",
    bullets: [
      "Unlimited users",
      "White-label custom domain",
      "Browser dialer",
      "Workspace data export + audit logs",
      "AI assist: $20 monthly gifted credit",
    ],
  },
];

export default function UpgradePage() {
  const { toast } = useToast();

  const checkout = useMutation({
    mutationFn: async (tier: "pro" | "agency") => {
      const r = await apiRequest("POST", "/api/upgrade/checkout-session", { tier });
      const j = await r.json();
      if (!j.url) throw new Error("No checkout URL returned.");
      window.location.href = j.url;
    },
    onError: (e: any) => toast({ title: "Checkout failed", description: String(e?.message || e), variant: "destructive" }),
  });

  const portal = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", "/api/upgrade/portal-link", {});
      const j = await r.json();
      if (!j.url) throw new Error("No portal URL returned.");
      window.location.href = j.url;
    },
    onError: (e: any) => toast({ title: "Portal failed", description: String(e?.message || e), variant: "destructive" }),
  });

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6" data-testid="page-upgrade">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold">Upgrade your plan</h1>
        <p className="text-muted-foreground">
          Pick the plan that fits your team. You can change or cancel anytime.
        </p>
      </header>

      <div className="grid md:grid-cols-3 gap-4">
        {TIERS.map((t) => (
          <Card key={t.tier} data-testid={`upgrade-tier-${t.tier}`}>
            <CardHeader>
              <CardTitle className="flex items-baseline justify-between">
                <span>{t.name}</span>
                <span className="text-base font-medium text-muted-foreground">{t.price}</span>
              </CardTitle>
              <CardDescription>{t.tier === "lite" ? "Current free tier" : `Subscribe to ${t.name}`}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                {t.bullets.map((b) => (
                  <li key={b} className="flex gap-2">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {b}
                  </li>
                ))}
              </ul>
              {t.tier === "lite" ? (
                <Button variant="outline" disabled className="w-full" data-testid="button-upgrade-lite-current">Current plan</Button>
              ) : (
                <Button
                  className="w-full"
                  onClick={() => checkout.mutate(t.tier)}
                  disabled={checkout.isPending}
                  data-testid={`button-upgrade-${t.tier}`}
                >
                  {checkout.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Upgrade to {t.name}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="card-billing-portal">
        <CardHeader>
          <CardTitle className="text-lg">Manage billing</CardTitle>
          <CardDescription>Update your payment method, view invoices, or cancel.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={() => portal.mutate()}
            disabled={portal.isPending}
            data-testid="button-billing-portal"
          >
            {portal.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Open billing portal
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
